import { OryxGameController } from "@westpierstudio/core-module-oryx/game/OryxGameController";
import { showActivateFreeRoundFinishedDialog, showGamingLimitsReachedErrorDialog, showGenericCommsErrorDialog, showInsufficientFundsDialog } from "@westpierstudio/core-module-oryx/utils/errorHandling";
import { get, asBoolean } from "@westpierstudio/core-module-oryx/services/rgsdata/URLParsing";
import { EventEmitter } from "@westpierstudio/luma/events";
import { reset, Resettable } from "@westpierstudio/luma/utils/reset";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { SlotGameUIView, UIBetConfig } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/SlotGameUIView";
import { PrinceOfThievesGameClient } from "game/PrinceOfThievesGameClient";
import { GameState, PrinceOfThievesGameModel } from "game/PrinceOfThievesGameModel";
import { PrinceOfThievesGameView } from "game/PrinceOfThievesGameView";
import { GameServices } from "./GameServices";
import { Reels } from "components/reels/Reels";
import { WinLines } from "components/winlines/WinLines";
import { CrossbowBonus } from "components/CrossbowBonus";
import { FreeSpinCounter } from "components/FreeSpinCounter";
import { BigWin } from "@westpierstudio/core-module-oryx/components/bigwin";
import { SoundPlayer } from "sound/SoundPlayer";
import { getSoundPlayer, setSoundPlayer } from "sound/SoundLocator";
import { FREE_ROUND, OpenGameEvent } from "servertypes/gameevents/OpenGame";
import { InitialSpinEvent } from "servertypes/gameevents/InitialSpin";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { SpinErrorEvent } from "servertypes/gameevents/SpinError";
import { GameEvent, GameRequest } from "servertypes/ServerEvents";
import { BetData, LastEvent, LastEventData, ReelSetSymbols, Win } from "servertypes/SharedTypes";
import { SpinType } from "components/reels/ReelsModel";
import { Signal } from "type-signals";
import { PeriodicInspectionEvent } from "@westpierstudio/core-module-oryx/services/comms/types/PeriodicInspection";
import { KSGLogo } from "components/KSGLogo";
import { GameDialogType } from "@westpierstudio/core-module-oryx/components/ui/dialog/DialogTypes";
import { PrinceOfThievesDebugGUI } from "gui/PrinceOfThievesDebugGUI";
import { allSignals } from "@westpierstudio/luma/utils/signal";
import { InitialSpinEventHandler } from "./eventhandlers/InitialSpinEventHandler";
import { FreeSpinEventHandler } from "./eventhandlers/FreeSpinEventHandler";
import { RetriggerWinPanel } from "components/RetriggerWinPanel";
import { TotalWinPanel as TotalBonusWinPanel } from "components/TotalWinPanel";
import { AnticipationView } from "components/AnticipationView";
import _ from "lodash";
import { GameLogo } from "components/GameLogo";
import { Background } from "components/Background";
import { ReelsBezel } from "components/reels/ReelsBezel";
import { Transition } from "components/Transition";
import { SkippableDelay } from "../utils/SkippableDelay";
import { CrossbowMultipliers } from "components/CrossbowMultipliers";
import { devLog } from "utils/Misc";
import { TouchStartFreeSpins } from "components/TouchStartFreeSpins";

type SessionCompleteEvent = InitialSpinEvent | FreeSpinEvent;

class GameSignals {
    skipBigWin = new Signal();
    skipBigWinEndOfFreeSpins = new Signal();
    skipWinRollUp = new Signal();
    skipSpin = new Signal();
    skipFreeSpinCounter = new Signal<(total: number) => void>();
    skipPanel = new Signal();
    skipCrossbow = new Signal();
    skipRetriggerBell = new Signal();
    skipLeafTransition = new Signal();
    startDelay = new Signal<(duration: number) => Signal>();
    startFreeSpinPrompt = new Signal();
    skipDelay = new Signal();
    skipFreeSpinPrompt = new Signal();
}

type EventHandlers = {
    initialSpin: InitialSpinEventHandler,
    freeSpin: FreeSpinEventHandler
}

export class PrinceOfThievesGameController extends OryxGameController<PrinceOfThievesGameView, PrinceOfThievesGameModel> {

    private resetList: Resettable[] = [];
    private flow!: Flow;
    private background!: Background;
    private transition!: Transition;
    private eventHandlers!: EventHandlers;
    private reels!: Reels;
    private winlines!: WinLines;
    private crossbowBonus!: CrossbowBonus;
    private freeSpinCounter!: FreeSpinCounter;
    private bigWin!: BigWin;
    private ksgLogo!: KSGLogo;
    private gameLogo!: GameLogo;
    private totalWinPanel!: TotalBonusWinPanel;
    private retriggerWinPanel!: RetriggerWinPanel;
    private soundplayer!: SoundPlayer;
    private touchStartFreeSpins!: TouchStartFreeSpins;
    private debugGUI?: PrinceOfThievesDebugGUI;
    signals = new GameSignals();
    skippableDelay = new SkippableDelay();

    constructor(protected override readonly services: GameServices, view: PrinceOfThievesGameView, model: PrinceOfThievesGameModel,
        gameEmitter: EventEmitter, protected override client: PrinceOfThievesGameClient) {
        super(services, view, model, gameEmitter, client);
    }

    override setup() {
        super.setup();

        this.resetList.push(this.flow = new Flow(this.gameEmitter));

        this.setupComponents();
        this.setupEventHandlers();
        this.resize();
        this.setupClient();
        this.setupSkip();
        this.setupGameSpecificDebugGUI();

        this.services.layout.onResize.add(this.resize, this);

        this.services.paytableContentFactory.loadPaytableAssets(this.services);
    }

    override initiateComms() {
        const showDebugUI = process.env.NODE_ENV === `production` ? false : asBoolean(get(`debugGUI`), true);
        if (showDebugUI) {
            this.setupDebugGUI();
        }

        this.client.establishConnection();
    }

    override reset() {
        super.reset();
        reset(this.resetList);

        this.client.establishConnection();
    }

    private setupClient(): void {

        this.client.signals.openGame.add(this.openGame, this);
        this.client.signals.initialSpin.add(this.initialSpin, this);
        this.client.signals.freeSpin.add(this.freeSpin, this);
        this.client.signals.spinError.add(this.spinError, this);
        this.client.signals.periodicInspection.add(this.periodicInspection, this);

        const openGameResponse = this.client.getLatestOpenGameResponse<OpenGameEvent>();
        if (openGameResponse) {
            this.openGame(openGameResponse);
        }
    }

    private setupDebugGUI(): void {
        const forcerGroups = this.services.resources.getSetting(`dev`).forcerGroups;
        const debugUI = this.debugGUI = new PrinceOfThievesDebugGUI(this.services, this.model);

        // delayed setup
        this.client.signals.openGame.once(event => debugUI.setupVersions(event.body.data.artifactVersions));
        this.client.signals.openGame.once(event => debugUI.setupRoundHistory(event.body.lastRoundId));
        this.client.adminSignals.forcerScenariosLoaded.once(scenarios => debugUI.setupForcer(scenarios, forcerGroups));

        // devui signals
        debugUI.signals.resetGame.add(() => this.resetGame.dispatch());
        debugUI.signals.force.add((value) => this.client.sendAdminForceRequest(value));
        debugUI.signals.setCurrencyCode.add(currencyCode => this.client.sendAdminSetCurrencyCodeRequest(currencyCode));

        this.client.signals.initialSpin.add(event => debugUI.setRoundID(event.body.data.roundId));
        this.client.signals.freeSpin.add(event => debugUI.setRoundID(event.body.data.roundId));

        debugUI.customSignals.setTheme.add((gameEvent) => {
            switch (gameEvent) {
                case GameEvent.INITIAL_SPIN:
                    this.background.setBackgroundBaseGame();
                    this.gameLogo.setSpinType(gameEvent);
                    this.crossbowBonus.visible = false;
                    break;
                case GameEvent.FREE_SPIN:
                    this.background.setBackgroundFreeGame();
                    this.gameLogo.setSpinType(gameEvent);
                    this.crossbowBonus.visible = true;
                    break;
                default:
                    break;
            }
        });
    }

    private setupGameSpecificDebugGUI() {
        if (!this.debugGUI) {
            return;
        }
        this.debugGUI.customSignals.printFlow.add(() => this.flow.printQueue());
        this.debugGUI.customSignals.setFlowVerbose.add(value => this.flow.setVerbose(value));
        const { reels, crossbowBonus, totalWinPanel, retriggerWinPanel, transition, soundplayer } = this;
        this.debugGUI.setupPrinceOfThieves({ crossbowBonus, retriggerWinPanel, totalWinPanel, reels, transition, soundplayer });
    }

    private setupComponents(): void {
        this.view.rootGameContent.addChild(this.background = new Background(this.services, this.services.layout));
        this.view.rootGameContent.addChild(this.reels = new Reels(this.services));
        this.view.rootGameContent.addChild(this.gameLogo = new GameLogo(this.services, this.reels));
        this.view.rootGameContent.addChild(this.freeSpinCounter = new FreeSpinCounter(this.services));
        this.view.rootGameContent.addChild(this.bigWin = new BigWin(this.services));
        this.view.rootGameContent.addChild(this.totalWinPanel = new TotalBonusWinPanel(this.services, this.services.layout));
        this.view.rootGameContent.addChild(this.retriggerWinPanel = new RetriggerWinPanel(this.services, this.services.layout));
        this.view.rootGameContent.addChild(this.touchStartFreeSpins = new TouchStartFreeSpins(this.services));
        this.view.rootGameContent.addChild(this.ksgLogo = new KSGLogo(this.services, this.services.layout));;
        this.view.rootGameContent.addChild(this.transition = new Transition(this.services, this.services.layout));

        const reelsLayout = this.model.reels.setupLayout();
        this.reels.setup(reelsLayout);
        this.winlines = this.reels.winlines;
        this.crossbowBonus = this.reels.crossbowBonus;

        setSoundPlayer(this.soundplayer = new SoundPlayer(this.services));

        this.resetList.push(this.reels);
        this.resetList.push(this.reels.jackpot);
        this.resetList.push(this.winlines);
        this.resetList.push(this.crossbowBonus);
        this.resetList.push(this.freeSpinCounter);
        this.resetList.push(this.bigWin);
        this.resetList.push(this.soundplayer);
        this.resetList.push(this.gameLogo);
        this.resetList.push(this.transition);
        this.resetList.push(this.retriggerWinPanel);
        this.resetList.push(this.totalWinPanel);

        this.bigWin.signals.countupComplete.add(this.clearSkip, this);
        this.bigWin.signals.sounds.playCountup.add(() => this.soundplayer.bigWinCountUp());
        this.bigWin.signals.sounds.stopCountup.add(() => this.soundplayer.stopBigWinCountUp());
        this.bigWin.signals.sounds.playSting.add(() => this.soundplayer.bigWinSting());
        this.model.signals.gameStateChange.add((state) => this.gameLogo.eventChange(state));

        this.signals.skipWinRollUp.add(() => this.totalWinPanel.slam());
        this.signals.skipWinRollUp.add(() => this.retriggerWinPanel.slam());

    }

    private setupEventHandlers(): void {

        const { model, client, reels, bigWin, winlines, crossbowBonus, freeSpinCounter, view, totalWinPanel, retriggerWinPanel, gameLogo, background, transition, touchStartFreeSpins } = this;
        const jackpot = reels.jackpot;

        this.eventHandlers = {
            initialSpin: new InitialSpinEventHandler(this.services, model, view, client, { reels, bigWin, winlines, gameLogo, background, transition, jackpot, crossbowBonus, freeSpinCounter, touchStartFreeSpins }),
            freeSpin: new FreeSpinEventHandler(this.services, this, model, view, client, { freeSpinCounter, reels, crossbowBonus, bigWin, winlines, totalWinPanel, retriggerWinPanel, gameLogo, background, transition, jackpot })
        };

        this.eventHandlers.initialSpin.signals.reelSpin.add((spinResult, spinType) => this.reelSpin(spinResult, spinType, GameEvent.INITIAL_SPIN));
        this.eventHandlers.initialSpin.signals.clearSkip.add(() => this.clearSkip());
        this.eventHandlers.initialSpin.signals.startBigWin.add((event) => this.startBigWin(event));
        this.eventHandlers.initialSpin.signals.showWinlines.add((event: InitialSpinEvent, spinType: SpinType) => this.showWinlines(event, spinType, true));
        this.eventHandlers.initialSpin.signals.stopWinAnims.add(() => this.stopWinAnims());
        this.eventHandlers.initialSpin.signals.sessionComplete.add((event) => this.sessionComplete(event));
        this.eventHandlers.initialSpin.signals.startLeafTransition.add(() => this.setSkip(this.signals.skipLeafTransition));
        this.eventHandlers.initialSpin.signals.startFreeSpinPrompt.add(() => this.setSkip(this.signals.skipFreeSpinPrompt));

        this.eventHandlers.freeSpin.signals.reelSpin.add((spinResult, spinType) => this.reelSpin(spinResult, spinType, GameEvent.FREE_SPIN));
        this.eventHandlers.freeSpin.signals.clearSkip.add(() => this.clearSkip());
        this.eventHandlers.freeSpin.signals.startBigWin.add((event) => this.startBigWinEndOfFreeSpins(event));
        this.eventHandlers.freeSpin.signals.showWinlines.add((event: FreeSpinEvent, spinType: SpinType) => this.showWinlines(event, spinType, false));
        this.eventHandlers.freeSpin.signals.stopWinAnims.add(() => this.stopWinAnims());
        this.eventHandlers.freeSpin.signals.sessionComplete.add((event) => this.sessionComplete(event));
        this.eventHandlers.freeSpin.signals.startShowPanel.add(() => this.setSkip(this.signals.skipPanel));
        this.eventHandlers.freeSpin.signals.startCrossbowBonus.add(() => this.setSkip(this.signals.skipCrossbow));
        this.eventHandlers.freeSpin.signals.startSkippableDelay.add((time) => this.signals.startDelay.dispatch(time));
        this.eventHandlers.freeSpin.signals.startRetrigger.add(() => this.setSkip(this.signals.skipRetriggerBell));
        this.eventHandlers.freeSpin.signals.updateBalance.add((event) => this.updateBalance(event.body.balance, event.body.balanceVersion));

        this.skippableDelay.complete.add(() => this.eventHandlers.freeSpin.signals.skippableDelayComplete.dispatch());
    }

    private setupSkip(): void {

        this.signals.skipBigWin.add(() => {
            this.skipBigWin();
            this.skipWinRollUp();
        });
        this.signals.skipBigWinEndOfFreeSpins.add(() => {
            this.skipBigWin();
        });

        this.signals.startDelay.add((time) => {
            this.skippableDelay.startSkippableDelay(time);
            this.setSkip(this.signals.skipDelay);
        });

        this.signals.skipFreeSpinPrompt.add(() => this.skipFreeSpinPrompt());

        if (this.services.rgsData.turboPlay) {

            this.signals.skipSpin.add(() => {
                this.slamStop(this.model.reels.spinResult as ReelSetSymbols);
            });
            this.signals.skipPanel.add(() => this.skipPanel());
            this.signals.skipCrossbow.add(() => this.skipCrossbow());
            this.signals.skipRetriggerBell.add(() => this.soundplayer.skipBonusBellRetrigger());
            this.signals.skipLeafTransition.add(() => this.skipLeafTransition());
            this.signals.skipWinRollUp.add(() => this.skipWinRollUp());
            this.signals.skipFreeSpinCounter.add((total: number) => this.skipFreeSpinCounter(total));

            this.signals.skipDelay.add(() => this.skippableDelay.skip());
        }
    }

    private openGame(event: OpenGameEvent): void {

        this.processFreeRounds(event);
        this.model.processOpenGameEvent(event);

        const { data, lastEvent, lastRoundId } = event.body;
        const { defaultReels, defaultBonusReels, defaultReelStrips } = data;

        this.setupGameUI(event, lastEvent?.data, lastRoundId);

        this.model.state = GameState.IDLE;
        this.reels.setReelStrips(defaultReelStrips);
        this.reels.setCurrentReelSymbols(defaultReels);
        this.reels.setDefaultBonusReels(defaultBonusReels);
        this.winlines.setup(this.model.reels.layout);

        this.services.gameBridge.sendGameBridgeEvent({ event: `setBalance`, payload: { balance: event.body.balance / this.services.currency.currencyConfig.minorDivisor } });
        this.services.gameBridge.sendGameBridgeEvent({ event: `gameReady` });
        this.services.gameBridge.sendGameBridgeEvent({ event: this.services.audio.soundEnabled ? `soundOn` : `soundOff` });

        this.loadingScreenHide.dispatch();

        if (lastEvent) {
            this.resumeGame(lastEvent);
        }
        else {
            this.background.setBackgroundBaseGame();
            this.gameLogo.baseGameIdleLoop(true);
            this.crossbowBonus.visible = false;
            this.reels.applyGamePadding(GameEvent.INITIAL_SPIN);
        }
    }

    private resumeGame(lastEvent: LastEvent): void {

        const nextAction = lastEvent.data.nextActions[0];
        console.log(`Last Event: ${lastEvent.event} Next: ${nextAction}`);

        this.flow.reset();

        switch (lastEvent.event) {
            case GameEvent.INITIAL_SPIN:
                this.eventHandlers.initialSpin.resume(this.flow, lastEvent);
                if (nextAction !== GameRequest.FREE_SPIN) {
                    this.background.setBackgroundBaseGame();
                }
                else {
                    this.crossbowBonus.visible = true;
                    this.gameLogo.resumeFreeGames();
                    this.background.setBackgroundFreeGame();
                    this.soundplayer.startBonusMusic();
                    this.reels.applyGamePadding(GameEvent.FREE_SPIN);
                    this.ksgLogo.resize(this.gameUI?.gameSafeArea);
                }
                break;
            case GameEvent.FREE_SPIN:
                this.eventHandlers.freeSpin.resume(this.flow, lastEvent);
                if (nextAction === GameRequest.FREE_SPIN) {
                    this.gameLogo.resumeFreeGames();
                    this.background.setBackgroundFreeGame();
                    this.soundplayer.startBonusMusic();
                    this.crossbowBonus.visible = true;
                    this.reels.applyGamePadding(GameEvent.FREE_SPIN);
                    this.ksgLogo.resize(this.gameUI?.gameSafeArea);
                }
                else {
                    this.background.setBackgroundBaseGame();
                    this.gameLogo.baseGameIdleLoop(true);
                }
                break;
            default:
                throw new Error(`Unhandled resume state`);
        }

        this.flow.run();
    }

    private initialSpin(event: InitialSpinEvent): void {

        this.setFreeRound(event.body.data.freeRounds);
        this.services.gameBridge.sendGameBridgeEvent({ event: `gameBusy` });

        this.model.processSpinEvent(event, false);

        const { balanceAfterBetData, roundId } = event.body.data;

        this.syncSessionElapsedTimeIfExists(event.body.elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);
        this.updateBalance(balanceAfterBetData.balanceAfterBet, balanceAfterBetData.balanceAfterBetVersion);

        this.eventHandlers.initialSpin.run(this.flow, event, this.gameUI as SlotGameUIView, this.services.rgsData.minTimeBetweenWagers);
    }

    private freeSpin(event: FreeSpinEvent): void {

        this.model.processSpinEvent(event, true);

        this.syncSessionElapsedTimeIfExists(event.body.elapsedTimeInMillis);
        this.eventHandlers.freeSpin.run(this.flow, event, this.gameUI as SlotGameUIView);
    }

    private reelSpin(spinResult: ReelSetSymbols, spinType: SpinType, eventType: GameEvent): void {

        const spinConfig = this.model.reels.setSpinConfigForSpinType(
            spinType,
            this.model.anticipation
        );

        if (spinConfig.logSpinTime) {
            this.model.reels.startTime = Date.now();
        }

        this.reels.setSpinConfig(spinConfig, spinResult, eventType);
        this.reels.spin(spinResult);
        getSoundPlayer().stopAllWinlineSounds();

        if (spinConfig.instantSlam) {
            this.slamStop(spinResult);
        }
        else {
            this.setSkip(this.signals.skipSpin);
        }

    }

    private startBigWin(event: InitialSpinEvent): void {

        const { balance, balanceVersion } = event.body;
        const { accumulatedMoneyWon, betData, totalMoneyWon } = event.body.data;

        this.bigWin.start(betData.playerBet, totalMoneyWon);
        this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, betData, false, true, false);
        this.bigWin.signals.complete.once(() => this.updateBalance(balance, balanceVersion));

        this.setSkip(this.signals.skipBigWin);
    }

    private startBigWinEndOfFreeSpins(event: FreeSpinEvent): void {

        const { balance, balanceVersion, data } = event.body;
        const { accumulatedMoneyWon, betData } = data;

        this.bigWin.start(betData.playerBet, accumulatedMoneyWon);
        this.bigWin.signals.complete.once(() => this.updateBalance(balance, balanceVersion));

        this.setSkip(this.signals.skipBigWinEndOfFreeSpins);
    }

    private showWinlines(event: InitialSpinEvent | FreeSpinEvent, spinType: SpinType, useBigWin: boolean): void {

        const { balance, balanceVersion } = event.body;
        const { accumulatedMoneyWon, betData, totalMoneyWon } = event.body.data;
        const isFreeSpin = spinType === SpinType.FREE_SPIN;

        const winDisplayConfig = this.model.winlines.setWinDisplayConfigForSpinType(spinType);
        this.winlines.setWinDisplayConfig(winDisplayConfig);

        this.model.state = GameState.SHOW_WINS;
        this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, betData, true, useBigWin, !isFreeSpin);

        this.reels.startWinAnims(this.model.winningSymbolState, totalMoneyWon, betData.playerBet);

        this.winlines.winLineLoop(this.model.winlines.winList);
    }

    private slamStop(spinResult: ReelSetSymbols): void {
        this.reels.slamStop(spinResult);
    }

    private skipFreeSpinCounter(total: number): void {
        this.freeSpinCounter.skip(total);
    }

    private skipBigWin(): void {

        this.bigWin.signals.skip.dispatch();
        this.soundplayer.stopBigWinCountUp();
    }

    private skipWinRollUp(): void {
        this.gameUI?.skipWinRollup();
    }

    private skipPanel() {
        this.retriggerWinPanel.signals.skip.dispatch();
        this.totalWinPanel.signals.skip.dispatch();
    }

    private skipFreeSpinPrompt(): void {
        this.touchStartFreeSpins.hide();
    }

    private skipCrossbow() {
        this.crossbowBonus.skip();
        this.setSkip(this.signals.skipCrossbow);
    }

    private skipLeafTransition() {
        this.transition.skipLeafTransition();
        this.setSkip(this.signals.skipLeafTransition);
    }

    private stopWinAnims(): void {

        this.winlines.reset();
        this.reels.stopWinAnims();
    }

    private resize(): void {

        const { layout } = this.services;

        this.freeSpinCounter.resize(layout.orientation);
        this.ksgLogo.resize(this.gameUI?.gameSafeArea);
    }

    private spinError(event: SpinErrorEvent): void {
        this.gameUI?.spinCancelled();
        switch (event.body.data.code) {
            case `OUT_OF_MONEY`:
                showInsufficientFundsDialog(this.services, this.gameDialogs);
                break;
            case `FREE_ROUND_FINISHED`:
                showActivateFreeRoundFinishedDialog(this.services, this.gameDialogs, this.resetGame);
                break;
            case `GAMING_LIMITS_REACHED`:
                showGamingLimitsReachedErrorDialog(this.services, this.gameDialogs);
                break;
            default:
                console.warn(`Unexpected Spin Error`, event);
                showGenericCommsErrorDialog(this.services, this.gameDialogs, this.resetGame);
                break;
        }
    }

    private setupGameUI(event: OpenGameEvent, resumeData?: LastEventData, lastRoundId?: string): void {

        if (this.gameUI) {

            const { balance, balanceVersion, lastEvent, netPosition } = event.body;
            const { rtp } = event.body.data;
            const { coinsPerBet, coinSizes, defaultCoinSize } = event.body.data.betConfig;

            let selectedCoin = defaultCoinSize;
            if (resumeData) {
                selectedCoin = resumeData.betData.coinSize;
            }
            if (event.body.data.FREE_ROUNDS && event.body.data.FREE_ROUNDS[0]) {
                const freeRound = event.body.data.FREE_ROUNDS[0];
                selectedCoin = freeRound.conf.defaultCoinSize;
            }

            const betConfig: UIBetConfig<"default"> = {
                coinSizes,
                coinsPerBet,
                defaultCoinSize,
                configName: `default`
            };

            this.gameUI.init({
                betConfigs: [betConfig],
                selectedBetConfigName: betConfig.configName,
                selectedCoin,
                balance,
                balanceVersion,
                netProfit: netPosition?.netProfit || 0,
                inBrokenGame: lastEvent !== undefined,
                brokenGameBet: lastEvent ? lastEvent.data.betData.playerBet : undefined,
                win: resumeData?.accumulatedMoneyWon,
                rtp: rtp !== undefined ? rtp * 100 : undefined,
            });
            this.ksgLogo.resize(this.gameUI?.gameSafeArea);

            this.gameUI.signals.startSpin.add((spinData) => {

                this.stopWinAnims();

                this.model.state = GameState.SPIN;
                this.model.spinSpeedType = spinData.spinSpeed;
                this.model.spinModeType = spinData.spinMode;

                const betData: BetData = {
                    coinsPerBet: spinData.coinsPerBet,
                    coinSize: spinData.selectedCoin,
                    playerBet: spinData.totalBet,
                    pickIndex: null
                };

                this.client.sendSpinRequest(betData, this.model.freeRoundId);
            });

            this.gameUI.signals.betChanged.add((_betData) => {

                this.stopWinAnims();

                const openGameData = this.model.openGameData;
                this.reels.setCurrentReelSymbols(openGameData.defaultReels);
            });

            this.gameUI.signals.openDeposit.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `goToDeposit` }));
            this.gameUI.signals.playForReal.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `playCashMode` }));
            this.gameUI.signals.openGameHistory.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `openGameHistory` }));
            this.gameUI.signals.goToHome.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `showLobby` }));
            this.gameUI.signals.sounds.click.add(() => this.soundplayer.uiClick());
            this.gameUI.signals.sounds.spinClick.add(() => this.soundplayer.uiClick());
            this.gameUI.signals.winRollUpSkipEnable.add(() => this.setSkip(this.signals.skipWinRollUp));
            this.gameUI.signals.winRollupComplete.add(() => getSoundPlayer().stopCurrentRollup());

            this.syncSessionElapsedTimeIfExists(event.body.elapsedTimeInMillis);

            if (lastRoundId) {
                this.gameUI?.updateTransactionId(lastRoundId);
            }
        }
    }

    private updateGameUIWinnings(accumulatedMoneyWon: number, balance: number, balanceVersion: number, betData: BetData, playSound = true, useBigWinDuration = true, updateBalance = true): void {

        if (this.gameUI) {

            const duration = this.gameUI.setWin(accumulatedMoneyWon, useBigWinDuration);
            if (updateBalance) {
                this.updateBalance(balance, balanceVersion);
            };

            if (playSound) {
                this.soundplayer.winlineSound(this.model.winlines, betData.playerBet);
            }
        }
    }

    private setSkip<SignalType extends Signal>(signal: SignalType, ...args: Parameters<SignalType["dispatch"]>): void {
        // eslint-disable-next-line
        this.gameUI?.signals.skip.once(() => signal.dispatch.apply(signal, args));
        this.gameUI?.showSkipControls();
    }

    private clearSkip(): void {
        this.gameUI?.signals.skip.detachAll();
        this.gameUI?.hideSkipControls();
    }

    private periodicInspection(event: PeriodicInspectionEvent) {
        const { balance, balanceVersion, netPosition, elapsedTimeInMillis } = event.body;

        this.updateBalance(balance, balanceVersion);
        if (netPosition) {
            this.gameUI?.updateNetProfit(netPosition.netProfit);
        }
        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
    }

    public updateBalance(balance: number, balanceVersion: number) {
        if (this.gameUI?.updateBalance(balance, balanceVersion)) {
            this.services.gameBridge.sendGameBridgeEvent({ event: `setBalance`, payload: { balance: balance / this.services.currency.currencyConfig.minorDivisor } });
        }
    }

    private syncSessionElapsedTimeIfExists(elapsedTimeInMillis?: number): void {

        if (elapsedTimeInMillis !== undefined) {
            this.gameUI?.syncSessionElapsedTime(elapsedTimeInMillis);
        }
    }

    private sessionComplete(event: SessionCompleteEvent): void {

        const { netPosition } = event.body;
        const { accumulatedMoneyWon, nextActions } = event.body.data;

        if (netPosition) {
            this.gameUI?.updateNetProfit(netPosition.netProfit);
        }
        this.gameUI?.spinComplete(accumulatedMoneyWon);

        this.model.state = GameState.IDLE;

        if (!nextActions) {

            this.services.gameBridge.sendGameBridgeEvent({ event: `gameNotBusy` });
            this.services.gameBridge.sendGameBridgeEvent({ event: `gameActivity` });

            if (this.model.activeFreeRoundsFinished) {
                const winAmount = this.model.activeFreeRoundData.data.winAmount;
                const formattedWinAmount = this.services.currency.formatCoins(winAmount, { group: true, addTrailingZeros: true, trimIntegers: true });
                this.gameDialogs.show({
                    type: GameDialogType.FREE_ROUNDS_SUMMARY,
                    formattedWinAmount,
                    accept: () => {
                        this.model.freeRoundsComplete();
                        this.gameUI?.freeRoundsCompleted();
                    }
                });
            }
        }
    }

    applyPadding() {
        if (this.reels) {
            this.reels.applyGamePadding();
        }
    }

    //------Free Rounds------

    private processFreeRounds(event: OpenGameEvent) {

        if (!event.body.data.FREE_ROUNDS || !event.body.data.FREE_ROUNDS.length) {
            return;
        }
        const freeRound = event.body.data.FREE_ROUNDS[0];

        if (freeRound.type !== `NUM_ROUNDS`) {
            console.error(`Unsupported FREE_ROUND type "${freeRound.type}"`);
            this.client.sendActivateFreeRoundRequest(freeRound.id, `DECLINE`);
            return;
        }

        this.gameDialogs.show({
            type: GameDialogType.FREE_ROUNDS_OFFER,
            resumingFreeRounds: freeRound.status === `ACTIVE`,
            availableBonusSpins: freeRound.data.numLeft,
            accept: () => {
                if (freeRound.status === `PENDING`) {
                    this.client.sendActivateFreeRoundRequest(freeRound.id, `ACCEPT`);
                }
                this.gameUI?.freeRoundsStarted(freeRound.conf.betAmount, freeRound.data.numLeft);
            }
        });

        this.model.setFreeRoundData(freeRound);
    }

    private setFreeRound(freeRound?: FREE_ROUND): void {

        if (freeRound) {

            this.model.setFreeRoundData(freeRound);

            if (freeRound.type === `NUM_ROUNDS`) {
                this.gameUI?.freeRoundsSpinsLeftChanged(freeRound.data.numLeft);
            }
        }
    }
}

